import {Button, Checkbox, Typography, Tag, Select, Tooltip, Card } from "antd";
import "./styles.css";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { getBullets, setBullets, updateBullets } from "../../../store/actions/settings.actions";
import { useSelector } from "react-redux";
import { SaveOutlined } from "@ant-design/icons";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import BlurredBulletPicker from "./blurred_bullet_picker.png";
import { PremiumFeatureDisabled } from "../../Common/PremiumFeatureDisabled";
import { audioMessage as message } from "../../Common/Notifications/audioMessage";
import { PermissionedButton, PermissionOverlay } from "../../PermissionedComponents";
import { AdminNotice } from "../AdminNotice";
import { useAuth0 } from "@auth0/auth0-react";

const { Text, Title } = Typography;

export const ReportBulletPicker = () => {
  const dispatch = useDispatch();
  const [bulletList, setBulletList] = useState(null);
  const [displayOnMultiSelectOptions, setDisplayOnMultiSelectOptions] = useState(null);
  const [bulletDisplaySelections, setBulletDisplaySelections] = useState(null);
  
  const { user } = useAuth0();
  const isStaffEmail = user?.email?.endsWith("gpsdataviz.com");

  const {
      positions,
      all_metric_types,
      info: { display_name: teamName, bullet_picker_enabled},
      sport
    } = useSelector(({ team }) => team);

  const {
      loading: { teamSettings: loading },
      bullets,
      error
    } = useSelector(({ settings }) => settings);
      
  useEffect(() => {
    dispatch(getBullets());
  }, []);

  useEffect(() => {
    if (bullets && bullets.length > 0) {
      const localBulletCopy = structuredClone(bullets);
      if (all_metric_types && all_metric_types?.gps) {
        const gps_metric_keys = all_metric_types.gps.map(metric => metric.key);
        
        const final_bullets = localBulletCopy.map(bullet => {
          const bullet_meta = bullet.bullet_metadata;
          if (bullet_meta && Object.keys(bullet_meta).length > 0) {
            const required_metrics = bullet_meta.required_metrics;
            if (required_metrics && required_metrics.length > 0) {
              if (required_metrics.length == 1) {
                if (
                  !gps_metric_keys.includes(required_metrics[0])
                ) 
                {
                    bullet.disabled = true;
                }
                else {
                  bullet.disabled = false;
                }
              } else if (required_metrics.length == 2 && 
                !gps_metric_keys.includes(required_metrics[0]) &&
                !gps_metric_keys.includes(required_metrics[1])){
                  bullet.disabled = true;
              } else {
                bullet.disabled = false;
              }    
            } else {
              bullet.disabled = false;
            }
          } else {
            bullet.disabled = false;
          }
          return bullet;
        })
        setBulletList(final_bullets);
      }
      
      const bulletSelections = localBulletCopy.map(bullet => {
        const metadata = bullet.metadata;
        if (metadata) {
          let selectionValues = [];
          if (metadata?.overview) {
            selectionValues.push("overview");
          }
          if (metadata?.player) {
            selectionValues.push("player");
          }
          if (metadata?.position && metadata?.position["show"]) {
            if (metadata?.position?.position_list && metadata?.position?.position_list.length > 0) {
              selectionValues = [...selectionValues, ...metadata?.position?.position_list]
            } else {
              selectionValues.push("position");
            }
          }
          return selectionValues;
        }
      })
      setBulletDisplaySelections(bulletSelections);
    }

    if (sport) {
      if (sport === "football" && positions) {
        const positionalOptions = positions.map(pos => {
          return {
            label: <span>{pos}</span>,
            value: pos,
          }
        })
        let multiSelectOptions = [
          {
            label: <span>Please select where you want this bullet to displayed</span>,
            title: '',
            options: [
              {
                label: <span>Overview Page</span>,
                value: 'overview',
              },
              {
                label: <span>All Position Pages</span>,
                value: 'position',
              },
            ],
          },
          {
            label: <span>Specific Positions</span>,
            title: 'position',
            options: positionalOptions,
          },
        ]
        setDisplayOnMultiSelectOptions(multiSelectOptions);
      }

      if (sport !== "football") {
        let multiSelectOptions = [
          {
            label: <span>Please select where you want this bullet to displayed</span>,
            title: '',
            options: [
              {
                label: <span>Overview Page</span>,
                value: 'overview',
              },
              {
                label: <span>Player Page</span>,
                value: 'player',
              },
            ],
          },
        ]
        setDisplayOnMultiSelectOptions(multiSelectOptions);
      }
    }
  }, [bullets, sport, positions]);

  // Function to update list on drop
  const handleDrop = (droppedItem) => {
    // Ignore drop outside droppable container
    if (!droppedItem.destination) return;
    var updatedList = structuredClone(bulletList);
    // Remove dragged item
    const [reorderedItem] = updatedList.splice(droppedItem.source.index, 1);
    // Add dropped item
    updatedList.splice(droppedItem.destination.index, 0, reorderedItem);
    // Update order property for all items
    updatedList = updatedList.map((item, index) => ({ ...item, order: index + 1 }));

    const bulletSelections = updatedList.map(bullet => {
      const metadata = bullet.metadata;
      if (metadata) {
        let selectionValues = [];
        if (metadata?.overview) {
          selectionValues.push("overview");
        }
        if (metadata?.player) {
          selectionValues.push("player");
        }
        if (metadata?.position && metadata?.position["show"]) {
          if (metadata?.position?.position_list && metadata?.position?.position_list.length > 0) {
            selectionValues = [...selectionValues, ...metadata?.position?.position_list]
          } else {
            selectionValues.push("position");
          }
        }
        return selectionValues;
      }
    })
    setBulletDisplaySelections(bulletSelections);

    // Update State
    setBulletList(updatedList);
  };

  const handleBulletCheck = (checkedBullet, checked) => {
    let updateIdx;
    const updatedBullets = bulletList.map((bullet, index) => {
      if (bullet.key === checkedBullet?.key) {
        bullet.show = checked;
        bullet.metadata = {};
        updateIdx = index;
      }
      return bullet;
    });

    setBulletDisplaySelections(bulletDisplaySelections.map((selection, idx) => {
      if (idx === updateIdx) {
        return [];
      }

      return selection;
    })) 

    setBulletList(updatedBullets);
  }

  const handleDisplaySectionCheck = (checkedParentBullet, bulletIdx, checked) => {
    let oldSelection = bulletDisplaySelections[bulletIdx];
    let newSelection = [];
    if (oldSelection.includes("position")) {
      const posValues = checked.filter(val => val !== "overview" && val !== "position" && val !== "player")
      if (posValues && posValues.length > 0) {
        setBulletDisplaySelections(
          bulletDisplaySelections.map((selection, index) => {
            if (index === bulletIdx) {
              newSelection = checked.filter(s => s !== "position");
              return newSelection;
            }
            return selection;
          })
        );
      } else {
        setBulletDisplaySelections(
          bulletDisplaySelections.map((selection, index) => {
            if (index === bulletIdx) {
              newSelection = checked;
              return newSelection;
            }
            return selection;
          })
        );
      }
    } else {
      const posValues = oldSelection.filter(val => val !== "overview" && val !== "player")
      if (posValues && posValues.length > 0 && checked.includes("position")) {
        setBulletDisplaySelections(
          bulletDisplaySelections.map((selection, index) => {
            if (index === bulletIdx) {
              newSelection = checked.filter(s => !posValues.includes(s));
              return newSelection
            }
            return selection;
          })
        );
      } else {
        setBulletDisplaySelections(
          bulletDisplaySelections.map((selection, index) => {
            if (index === bulletIdx) {
              newSelection = checked;
              return newSelection;
            }
            return selection;
          })
        );
      }
    }

    const updatedBullets = bulletList.map((bullet) => {
      if (bullet.key === checkedParentBullet?.key) {
        if (newSelection.length > 0) {
          bullet.show = true;
        } else {
          bullet.show = false;
        }
        
        bullet.metadata = {};
        if (newSelection.includes('position')) {
          bullet.metadata = {...bullet.metadata, position: { show: true }};
        } 
        if (newSelection.includes('overview')){
          bullet.metadata = {...bullet.metadata, overview: true };
        } 
        if (newSelection.includes('player')){
          bullet.metadata = {...bullet.metadata, player: true };
        } 
        let posList = newSelection.filter(val => val !== "overview" && val !== "position" && val !== "player");
        if (posList && posList.length > 0) {
          bullet.metadata = {...bullet.metadata, position: { show: true, position_list: posList }};
        } 
      }
      return bullet;
    });

    setBulletList(updatedBullets);
  }

  const handleSaveChanges = () => {
    let bulletsNames = []
    const updatedBullets = structuredClone(bulletList);
    const bulletsWithBlankMeta = updatedBullets.filter(bullet => {
      let metadata = bullet.metadata;
      let invalidBulletChecks = (metadata && typeof metadata === 'object' && Object.keys(metadata).length === 0);
      if (bullet.show) {
        if (invalidBulletChecks){
          bulletsNames.push(bullet.name);
          return bullet;
        }

        const metaKeys = Object.keys(metadata); 
        if (sport == "football") {
          if(
              metaKeys.length == 2 && 
              (
                (metaKeys.includes("overview") && !metadata["overview"]) && 
                (metaKeys.includes("position") && !metadata["position"]["show"])
              )
            ) {
                bulletsNames.push(bullet.name);
                return bullet;
          }

          if(
              metaKeys.length == 1 && 
              (
                (metaKeys.includes("overview") && !metadata["overview"]) ||
                (metaKeys.includes("position") && !metadata["position"]["show"])
              )
           ) {
                bulletsNames.push(bullet.name);
                return bullet;
          }
        } else {
          if(
              metaKeys.length == 2 && 
              (
                (metaKeys.includes("overview") && !metadata["overview"]) && 
                (metaKeys.includes("player") && !metadata["player"])
              )
           ) {
              bulletsNames.push(bullet.name);
              return bullet;
          }

          if(
              metaKeys.length == 1 && 
              (
                (metaKeys.includes("overview") && !metadata["overview"]) || 
                (metaKeys.includes("player") && !metadata["player"])
              )
           ) {
              bulletsNames.push(bullet.name);
              return bullet;
          }
        }
      }
    })
    
    if (bulletsWithBlankMeta && bulletsWithBlankMeta.length > 0) {
      message.error(`Please make valid selections for bullets: ${bulletsNames.join(', ')}`)
      return
    }

    const originalBulletsByKey = Object.fromEntries(bullets.map(i => [i.key, i]));
    const bullet_list = updatedBullets.filter((bullet) => {
      const original_bullet = originalBulletsByKey[bullet.key];
      let new_bullet = {};
      if (original_bullet["show"] != bullet["show"]) {
        new_bullet.id = bullet.id;
        new_bullet.show = bullet.show;
        new_bullet.metadata = bullet.metadata;
      } 

      if (original_bullet["order"] && original_bullet["order"] != bullet["order"]) {
        new_bullet.id = bullet.id;
        new_bullet.order = bullet.order;
      }

      if (JSON.stringify(original_bullet["metadata"]) != JSON.stringify(bullet["metadata"])) {
        new_bullet.id = bullet.id;
        new_bullet.metadata = bullet.metadata;
      }

      if (Object.keys(new_bullet).length > 0)
        return new_bullet;
      
    });

    let final_bullets = bullet_list.map(b => {
      
      return {
        id: b.id,
        metadata: b.metadata,
        order: b.order,
        show: b.show
      }
    });
    
    dispatch(updateBullets(final_bullets));
    dispatch(setBullets(updatedBullets));
  }

  if (!bullet_picker_enabled && !isStaffEmail) {
    return (
      <PremiumFeatureDisabled
        bg_image={BlurredBulletPicker}
        show_navbar={false}
        feature_name={"Pick Your Bullets"}
        info_text={<>
        This is a premium feature that will allow you to customize which insight bullets you see on your GPS reports. <br></br>
        If you are interested in adding this to your platform, please contact your sales rep!</>}
      />
    )
  }

  return (
    <div className="bullets-container">
      {(!bullet_picker_enabled && isStaffEmail) && (
        <AdminNotice style={{ marginBottom: '16px' }} />
      )}
      <PermissionedButton
        icon={<SaveOutlined />}
        permissionCheck="EditReportBullets"
        tooltipMessage="You don't have permission to edit report bullets"
        type="primary"
        onClick={handleSaveChanges}
      >
        Save Changes
      </PermissionedButton>
      <PermissionOverlay
        permissionCheck="EditReportBullets"
        tooltipMessage="You don't have permission to edit report bullets"
      >
      <DragDropContext onDragEnd={handleDrop}>
            <Droppable droppableId="list-container">
              {(provided) => {
                return (
                  <div
                    className="list-container"
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {bulletList?.map((bullet, index) => (
                      <Draggable
                        key={bullet.id}
                        draggableId={`${bullet?.id}`}
                        index={index}
                      >
                        {(provided) => (
                          <Tooltip 
                            title={bullet.disabled ? "This bullet requires a metric that is not assigned to this team" : null} 
                            placement="left" 
                            key={bullet.id}>
                          <div
                            className={`bullet-container ${bullet.disabled ? "bullet-disabled" : ""}`}
                            ref={provided.innerRef}
                            {...provided.dragHandleProps}
                            {...provided.draggableProps}
                          >
                            <div className="title-displayon-container">
                              <Checkbox disabled={bullet.disabled} checked={bullet?.show} onChange={(e) => handleBulletCheck(bullet, e.target.checked)}>
                                <Title level={5}>{bullet?.name}</Title>
                              </Checkbox>
                              <Select
                                placeholder={<span>Report Page</span>}
                                mode="multiple"
                                className="option-group-select"
                                onChange={(checked) => handleDisplaySectionCheck(bullet, index, checked)}
                                value={bulletDisplaySelections[index]}
                                style={{ width: '350px' }}
                                options={displayOnMultiSelectOptions}
                              >
                              </Select>
                            </div>
                            <div className="bullet-example-text" dangerouslySetInnerHTML={{ __html: bullet?.example }}></div>
                          </div>
                          </Tooltip>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                );
              }}
            </Droppable>
      </DragDropContext>
      </PermissionOverlay>
    </div>
  );
};
